declare global {
  interface Window {
    Telegram: {
      WebApp: {
        requestFullscreen: () => void;
        setHeaderColor: (color: string) => void;
        ready: () => void;
        expand: () => void;
        platform: string;
        isExpanded: boolean;
        disableVerticalSwipes: () => void;
        shareLink?: (url: string, options?: { text?: string }) => void;
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged: () => void;
        };
        contentSafeAreaInset: {
          top: number;
          right: number;
          bottom: number;
          left: number;
        };
        MainButton: {
          show: () => void;
          hide: () => void;
          setText: (text: string) => void;
          onClick: (callback: () => void) => void;
        };
      };
    };
  }
}

// ❌ СТАРЫЙ КОД (ВЫЗЫВАЕТ ОШИБКУ):
// export const telegram = window.Telegram.WebApp;

// ✅ НОВЫЙ КОД: Ленивая инициализация с проверкой
let telegramInstance: typeof window.Telegram.WebApp | null = null;
let initializationPromise: Promise<typeof window.Telegram.WebApp> | null = null;

/**
 * Получить экземпляр Telegram WebApp с проверкой готовности
 */
function getTelegram(): typeof window.Telegram.WebApp {
  // Если уже инициализирован, возвращаем сразу
  if (telegramInstance) {
    return telegramInstance;
  }

  // Проверяем доступность
  if (window.Telegram?.WebApp) {
    telegramInstance = window.Telegram.WebApp;
    return telegramInstance;
  }

  // Если SDK не загружен, выбрасываем ошибку с подробной информацией
  console.error('❌ Telegram WebApp SDK not loaded!');
  console.error('Debug info:', {
    hasTelegram: !!window.Telegram,
    hasWebApp: !!window.Telegram?.WebApp,
    userAgent: navigator.userAgent
  });

  throw new Error('Telegram WebApp SDK is not available. Make sure the script is loaded in index.html');
}

/**
 * Асинхронная инициализация с ожиданием загрузки SDK
 */
export function waitForTelegram(maxAttempts = 50, interval = 100): Promise<typeof window.Telegram.WebApp> {
  // Если уже есть promise инициализации, возвращаем его
  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = new Promise((resolve, reject) => {
    let attempts = 0;

    const checkTelegram = () => {
      if (window.Telegram?.WebApp) {
        telegramInstance = window.Telegram.WebApp;
        console.log('✅ Telegram WebApp SDK loaded successfully');
        console.log('Platform:', telegramInstance.platform);
        resolve(telegramInstance);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkTelegram, interval);
      } else {
        const error = new Error(`Telegram WebApp SDK failed to load after ${maxAttempts * interval}ms`);
        console.error('❌', error.message);
        console.error('Debug info:', {
          hasTelegram: !!window.Telegram,
          userAgent: navigator.userAgent,
          attempts
        });
        reject(error);
      }
    };

    checkTelegram();
  });

  return initializationPromise;
}

// Экспортируем геттер вместо прямого объекта
export const telegram = {
  get instance() {
    return getTelegram();
  }
};

export const haptic = {
  impact() {
    try {
      getTelegram().HapticFeedback.impactOccurred('light');
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  },

  medium() {
    try {
      getTelegram().HapticFeedback.impactOccurred('medium');
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  },

  heavy() {
    try {
      getTelegram().HapticFeedback.impactOccurred('heavy');
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  },

  success() {
    try {
      getTelegram().HapticFeedback.notificationOccurred('success');
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  },

  error() {
    try {
      getTelegram().HapticFeedback.notificationOccurred('error');
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  },

  warning() {
    try {
      getTelegram().HapticFeedback.notificationOccurred('warning');
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  },

  selection() {
    try {
      getTelegram().HapticFeedback.selectionChanged();
    } catch (error) {
      console.error('Haptic feedback not supported:', error);
    }
  }
};

interface SafeAreaValue {
  value: number;
  unit: 'px' | 'vh' | 'vw';
}

interface SafeAreaInsets {
  top?: SafeAreaValue;
  right?: SafeAreaValue;
  bottom?: SafeAreaValue;
  left?: SafeAreaValue;
}

const customInsets: SafeAreaInsets = {
  top: { value: 0, unit: 'px' },
  right: { value: 0, unit: 'px' },
  bottom: { value: 0, unit: 'px' },
  left: { value: 0, unit: 'px' }
};

export const getSafeAreaInsets = (overrides: SafeAreaInsets = {}) => {
  const insets = {
    top: overrides.top ?? customInsets.top,
    right: overrides.right ?? customInsets.right,
    bottom: overrides.bottom ?? customInsets.bottom,
    left: overrides.left ?? customInsets.left
  };

  return insets;
};

export const isMobileDevice = () => {
  try {
    const platform = getTelegram().platform || '';
    return platform === 'android' || platform === 'ios';
  } catch {
    return false;
  }
};

export const requestFullscreen = () => {
  if (isMobileDevice()) {
    try {
      getTelegram().requestFullscreen();
      getTelegram().setHeaderColor('#2a2a2a');
    } catch (error) {
      console.error('Error requesting fullscreen:', error);
    }
  }
};

export const initTelegram = async () => {
  try {
    // Ждём загрузки SDK
    const tg = await waitForTelegram();

    // Инициализируем
    tg.ready();

    if (isMobileDevice()) {
      tg.expand();
    }

    console.log('✅ Telegram initialized successfully');
    return tg;
  } catch (error) {
    console.error('❌ Failed to initialize Telegram:', error);
    throw error;
  }
};

export const disableSwipes = () => {
  try {
    const tg = getTelegram();
    if (tg.disableVerticalSwipes) {
      tg.disableVerticalSwipes();
    }
  } catch (error) {
    console.error('Error disabling swipes:', error);
  }
};